import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  CheckCircle2,
  Loader2,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';

type ApprovalStatus = 'loading' | 'success' | 'error';

export default function ApproveCountRequest() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<ApprovalStatus>('loading');
  const [shortCode, setShortCode] = useState('');
  const [shareToken, setShareToken] = useState('');
  const [itemCount, setItemCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No approval token provided');
      return;
    }

    const approve = async () => {
      try {
        const { data, error } = await supabase.rpc('approve_count_request', {
          p_approval_token: token,
        });

        if (error) throw error;

        setShortCode(data.short_code || '');
        setShareToken(data.share_token || '');
        setItemCount(data.item_count || 0);
        setStatus('success');
      } catch (err: any) {
        setErrorMessage(err.message || 'Failed to approve request');
        setStatus('error');
      }
    };

    approve();
  }, [token]);

  const countUrl = shortCode
    ? `${window.location.origin}/c/${shortCode}`
    : shareToken
      ? `${window.location.origin}/cashier-count/${shareToken}`
      : '';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Loading */}
        {status === 'loading' && (
          <div className="rounded-xl border bg-card p-8 text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <h2 className="text-lg font-semibold">Approving Request...</h2>
            <p className="text-sm text-muted-foreground">
              Creating the stock count with inventory data...
            </p>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="rounded-xl border bg-card p-8 text-center space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 mx-auto">
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-lg font-semibold">Request Approved</h2>
            <p className="text-sm text-muted-foreground">
              A stock count has been created with {itemCount} inventory items pre-populated from
              the current Front of House stock. The cashier has been notified automatically.
            </p>
            {countUrl && (
              <div className="space-y-3 pt-2">
                <Button asChild className="w-full">
                  <a href={countUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Count
                  </a>
                </Button>
                <Button variant="outline" asChild className="w-full">
                  <Link to="/stock-control/cashier-counts">
                    Go to Dashboard
                  </Link>
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="rounded-xl border bg-card p-8 text-center space-y-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 mx-auto">
              <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-lg font-semibold">Approval Failed</h2>
            <p className="text-sm text-muted-foreground">
              {errorMessage}
            </p>
            <p className="text-xs text-muted-foreground">
              This link may have already been used, expired, or the request was already handled.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
